import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { fetchPublicFaq } from '../services/api.js';

// Public, unauthenticated marketing FAQ. Pulls from /public/faq which serves
// the FAQs of the Relay Work tenant (or whatever PUBLIC_FAQ_TENANT_ID
// points at on the API).

export default function Faq() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchPublicFaq();
        if (!cancelled) setFaqs(r?.data || []);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load FAQ.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const grouped = useMemo(() => {
    const by = {};
    for (const f of faqs) {
      const c = f.category || 'General';
      (by[c] = by[c] || []).push(f);
    }
    return Object.entries(by);
  }, [faqs]);

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100">
      <header className="border-b border-stone-900">
        <div className="mx-auto max-w-4xl px-6 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-alfred-gold font-serif text-2xl">Alfred</span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-stone-500">
              Voice
            </span>
          </Link>
          <Link to="/login" className="text-sm text-stone-300 hover:text-alfred-gold">
            Sign in
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-14">
        <h1 className="text-4xl font-serif">Frequently asked questions</h1>
        <p className="mt-3 text-stone-400 max-w-2xl">
          Quick answers about pricing, security, and how Alfred Voice handles real
          business calls. Still have a question?{' '}
          <a href="mailto:hello@relaywork.co" className="text-alfred-gold hover:underline">
            Email us
          </a>
          .
        </p>

        {error && (
          <p className="mt-6 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            {error}
          </p>
        )}

        {loading ? (
          <p className="mt-10 text-stone-500">Loading…</p>
        ) : grouped.length === 0 ? (
          <p className="mt-10 text-stone-500">No FAQs are published yet — check back soon.</p>
        ) : (
          <div className="mt-10 space-y-10">
            {grouped.map(([cat, list]) => (
              <section key={cat}>
                <h2 className="text-xs uppercase tracking-[0.25em] text-alfred-gold mb-3">
                  {cat}
                </h2>
                <ul className="rounded-2xl border border-stone-800 divide-y divide-stone-900 overflow-hidden">
                  {list.map((f) => {
                    const isOpen = !!open[f.id];
                    return (
                      <li key={f.id}>
                        <button
                          type="button"
                          onClick={() => setOpen((o) => ({ ...o, [f.id]: !o[f.id] }))}
                          className="w-full flex items-start justify-between gap-4 px-6 py-5 text-left hover:bg-stone-900/50 transition"
                          aria-expanded={isOpen}
                        >
                          <span className="text-stone-100 font-medium">{f.question}</span>
                          <ChevronDown
                            size={18}
                            className={`shrink-0 mt-0.5 text-stone-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                          />
                        </button>
                        {isOpen && (
                          <div className="px-6 pb-5 text-stone-300 leading-relaxed whitespace-pre-wrap">
                            {f.answer}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </main>

      <footer className="border-t border-stone-900 mt-16">
        <div className="mx-auto max-w-4xl px-6 py-8 text-xs text-stone-500 flex items-center justify-between">
          <span>© Relay Work · Alfred Voice</span>
          <span className="space-x-4">
            <Link to="/privacy" className="hover:text-alfred-gold">Privacy</Link>
            <Link to="/terms" className="hover:text-alfred-gold">Terms</Link>
            <Link to="/login" className="hover:text-alfred-gold">Customer sign-in →</Link>
          </span>
        </div>
      </footer>
    </div>
  );
}
